import CloseOutlined from "@mui/icons-material/CloseOutlined"
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Grid,
	IconButton,
	Slide,
	Typography,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { TransitionProps } from "@mui/material/transitions"
import { AxiosError } from "axios"
import { MuiOtpInput } from "mui-one-time-password-input"
import { forwardRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"

import { AppDispatch } from "../../store"
import { verifyOtpToApprove } from "../../store/actions/approve"
import { FileState } from "../../store/actions/file"
import { updateStateForId } from "../../store/files-list/files-slice"

interface IModalOtpProps {
	fileInfo: FileState
	isAuthorized: boolean | undefined
	openOtpDrawer: boolean
	setOpenOtpDrawer: (value: boolean) => void
}

const Transition = forwardRef(function Transition(
	props: TransitionProps & {
		children: React.ReactElement<any, any>
	},
	ref: React.Ref<unknown>
) {
	return <Slide direction="up" ref={ref} {...props} />
})

const ModalOtp = ({
	fileInfo,
	isAuthorized,
	openOtpDrawer,
	setOpenOtpDrawer,
}: IModalOtpProps) => {
	const theme = useTheme()
	const dispatch = useDispatch<AppDispatch>()
	const { t } = useTranslation()
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [otp, setOtp] = useState<string | undefined>(undefined)
	const [pasted, setPasted] = useState<string | undefined>(undefined)
	const [errorMessage, setErrorMessage] = useState<string | undefined>(
		undefined
	)

	const handleClose = () => {
		setErrorMessage(undefined)
		setOtp(undefined)
		setPasted(undefined)
		setOpenOtpDrawer(false)
	}

	const handleOtp = (event: React.FormEvent) => {
		event.preventDefault()
	}

	const matchIsNumeric = (text: string) => {
		const isNumber = typeof text === "number"
		return (isNumber || text !== "") && !Number.isNaN(Number(text))
	}

	const validateChar = (value: string) => {
		return matchIsNumeric(value.trim())
	}

	const clearForm = () => {
		setOtp("")
		setErrorMessage(undefined)
		setPasted(undefined)
	}

	const handleChange = (newValue: string) => {
		const userValue: string = pasted || newValue.trim()
		setErrorMessage(undefined)
		setOtp(userValue)
		setPasted(undefined)
	}

	const requestVerifyOtpToApprove = () => {
		if (isAuthorized === undefined || otp === undefined) return
		setIsLoading(true)
		dispatch(
			verifyOtpToApprove({
				bankId: fileInfo.idBank,
				corporateId: fileInfo.corporateSenderId,
				fileId: fileInfo.id.toString(),
				isAuthorized: isAuthorized,
				otpValue: otp,
			})
		)
			.unwrap()
			.then((result: { status: number; message: string }) => {
				if (result.status === 200) {
					dispatch(
						updateStateForId({
							id: fileInfo.id,
							newState: result.message.status,
						})
					)
					setOpenOtpDrawer(false)
					// TODO message ok
				} else {
					setIsLoading(false)
					setErrorMessage(result?.message)
				}
			})
			.catch((error: AxiosError<string>) => {
				// setOpenOtpDrawer(false)
				setErrorMessage(t("approve.error.generic", { ns: "distinte" }))
				console.log("ERROR: " + error)
			})
	}

	return (
		<Dialog
			open={openOtpDrawer}
			TransitionComponent={Transition}
			keepMounted
			onClose={() => {
				setIsLoading(false)
				handleClose()
			}}
			aria-describedby="alert-dialog-slide-description"
		>
			<DialogTitle>{t("approve.dialog.title", { ns: "distinte" })}</DialogTitle>
			<form
				id="approveOtpForm"
				onSubmit={(event: React.FormEvent) => {
					event.preventDefault()
					requestVerifyOtpToApprove()
				}}
			>
				<DialogContent style={{ width: "480px" }}>
					<DialogContentText id="alert-dialog-slide-description">
						{t("approve.dialog.description", { ns: "distinte" })}
					</DialogContentText>
					<IconButton
						aria-label="close"
						onClick={() => {
							setIsLoading(false)
							handleClose()
						}}
						sx={(theme) => ({
							position: "absolute",
							right: 8,
							top: 8,
							color: theme.palette.grey[500],
						})}
					>
						<CloseOutlined />
					</IconButton>

					<Grid container spacing={0} direction="column" gap={"16px"}>
						<Grid item xs={12} width={"100%"}>
							<MuiOtpInput
								value={otp}
								onChange={handleChange}
								length={6}
								validateChar={validateChar}
							/>
							{errorMessage && (
								<Typography component="span" variant="caption" color="error">
									{t(`approve.${errorMessage?.message}`, { ns: "distinte" })}
								</Typography>
							)}
						</Grid>
					</Grid>

					<Grid container justifyContent="flex-end"></Grid>
				</DialogContent>
				<DialogActions>
					<Grid container justifyContent={"space-between"}>
						{isLoading ? (
							<>
								<div></div>
								<CircularProgress size={32} sx={{}} />
							</>
						) : (
							<>
								<Button onClick={() => clearForm()}>
									{t("approve.buttons.cancel", { ns: "distinte" })}
								</Button>
								<Button
									type="submit"
									disabled={!otp}
									style={{
										backgroundColor: theme.palette.primary.main,
										color: theme.palette.primary.contrastText,
									}}
								>
									{t("approve.buttons.send", { ns: "distinte" })}
								</Button>
							</>
						)}
					</Grid>
				</DialogActions>
			</form>
		</Dialog>
	)
}

export default ModalOtp
